// Comments section — used only inside the member article detail page.
// Reminder text is a NON-blocking hint aligned with the already-accepted
// community_privacy_terms (§22). Never re-prompts terms acceptance.

import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/modules/growing-circle/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MAX = 5000;
const REMINDER = "Please do not share health, therapeutic or other sensitive personal information, or confidential/personal information concerning third parties.";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function CommentRow({ c, onEdit, onDelete }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(c.body);
  const created = c.created_at ? new Date(c.created_at).getTime() : 0;
  const withinWindow = Date.now() - created < 15 * 60 * 1000;
  const editable = c.is_owner && withinWindow && (c.status === "approved" || c.status === "pending");
  return (
    <li className="border-t border-white/10 pt-4 pb-4" data-testid={`comment-${c.id}`}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm font-medium">{c.user_name}</span>
        <span className="text-xs text-muted-foreground">{fmt(c.created_at)}{c.updated_at ? " · edited" : ""}</span>
      </div>
      {c.status === "pending" && (
        <span className="inline-block text-[0.65rem] uppercase tracking-[0.14em] text-amber-500 mb-2">Awaiting moderation</span>
      )}
      {edit ? (
        <>
          <Textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                    maxLength={MAX} rows={4} className="mb-2" data-testid={`comment-edit-input-${c.id}`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={async () => { await onEdit(c.id, draft); setEdit(false); }}
                    data-testid={`comment-save-${c.id}`}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setDraft(c.body); setEdit(false); }}
                    data-testid={`comment-cancel-${c.id}`}>Cancel</Button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{c.body}</p>
          {c.is_owner && (
            <div className="flex gap-3 mt-2 text-xs">
              {editable && (
                <button className="text-muted-foreground hover:text-foreground underline"
                        onClick={() => setEdit(true)} data-testid={`comment-edit-btn-${c.id}`}>Edit</button>
              )}
              <button className="text-muted-foreground hover:text-destructive underline"
                      onClick={() => onDelete(c.id)} data-testid={`comment-delete-${c.id}`}>Delete</button>
            </div>
          )}
        </>
      )}
    </li>
  );
}

export default function CommentSection({ slug, articleId }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/articles/${slug}/comments`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  useEffect(() => { if (slug) load(); }, [slug]);

  const submit = async () => {
    if (!draft.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/articles/${slug}/comments`, { body: draft.trim() });
      setDraft("");
      await load();
      toast.success("Reflection submitted.");
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "rate_limited") toast.error("You've reached the limit of 5 reflections per hour.");
      else toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const onEdit = async (cid, body) => {
    try {
      await api.put(`/articles/comments/${cid}`, { body });
      await load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "edit_window_expired") toast.error("The edit window has expired.");
      else toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const onDelete = async (cid) => {
    if (!window.confirm("Delete this reflection?")) return;
    try { await api.delete(`/articles/comments/${cid}`); await load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const remaining = MAX - draft.length;
  return (
    <section className="max-w-2xl mx-auto px-5 pb-16" data-testid="comment-section">
      <h2 className="font-heading text-xl tracking-tight mb-4">Reflections</h2>
      <p className="text-xs text-muted-foreground italic mb-4" data-testid="comment-reminder">{REMINDER}</p>
      <div className="mb-8">
        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={MAX} rows={4}
                  placeholder="Share your reflection…"
                  data-testid="comment-input" />
        <div className="flex justify-between items-center mt-2">
          <span className="text-xs text-muted-foreground">{remaining} characters remaining</span>
          <Button onClick={submit} disabled={!draft.trim() || submitting}
                  className="btn-gold rounded-full h-10 px-5 text-xs uppercase tracking-[0.14em]"
                  data-testid="comment-submit">Post reflection</Button>
        </div>
      </div>
      <ul className="space-y-0" data-testid="comment-list">
        {items.map((c) => <CommentRow key={c.id} c={c} onEdit={onEdit} onDelete={onDelete} />)}
        {items.length === 0 && <li className="text-sm text-muted-foreground py-6" data-testid="comment-empty">No reflections yet.</li>}
      </ul>
    </section>
  );
}
